import { boxFromLayout, ImagePinComponentRenderer, isPlaceholderImage, scaleSize } from '@lessonup/pin-renderer';
import { ImagePinComponent, LU_PIN_DIMENSIONS } from '@lessonup/pins-shared';
import { css, styled, useDetectSimpleClick } from '@lessonup/ui-components';
import React, { useCallback } from 'react';
import type { FocusMode } from '../../store/playerStoreFocusMode.types';

export interface ImagePinComponentInteractiveProps {
  pinComponent: ImagePinComponent;
  onOpenFocusMode: (focusMode: FocusMode) => void;
}

export const ImagePinComponentInteractive: React.FC<ImagePinComponentInteractiveProps> = ({
  pinComponent,
  onOpenFocusMode,
}) => {
  const box = boxFromLayout(pinComponent.layout);
  const allowZoom =
    pinComponent.settings.zoomableInInteractiveViewer !== false && !isPlaceholderImage(pinComponent.settings);

  const onClick = useCallback(() => {
    const scaleFactor = Math.min(LU_PIN_DIMENSIONS.width / box.size.width, LU_PIN_DIMENSIONS.height / box.size.height);
    const size = scaleSize(box.size, scaleFactor);

    onOpenFocusMode({
      type: 'IMAGE',
      pinComponent: {
        ...pinComponent,
        layout: {
          ...box,
          size,
        },
      },
    });
  }, [box, onOpenFocusMode, pinComponent]);

  const { handleMouseDown, handleMouseUp } = useDetectSimpleClick(onClick);

  return (
    <StyledImagePinComponentWrapper allowZoom={allowZoom}>
      <ImagePinComponentRenderer
        settings={pinComponent.settings}
        layout={box}
        onMouseDown={allowZoom ? handleMouseDown : undefined}
        onMouseUp={allowZoom ? handleMouseUp : undefined}
      />
    </StyledImagePinComponentWrapper>
  );
};

const StyledImagePinComponentWrapper = styled.div<{ allowZoom: boolean }>`
  ${(props) =>
    props.allowZoom &&
    css`
      cursor: zoom-in;
    `}
  width: 100%;
  height: 100%;
`;
